import { DateTime } from "luxon";
import { RouteState, RouteStateType } from "service";
import { AdminGetRouteStatusesPerMonthResDto } from "service/api/estabrookSchemas";
import { AppTheme } from "ui";

export interface StateColors {
  borderColor: string;
  color: string;
  backgroundColor: string;
}

export const getRouteStateColors = (
  theme: AppTheme,
  state: RouteStateType | undefined,
  isCurrentlySelected = false,
): StateColors => {
  const { palette } = theme;

  switch (state) {
    case RouteState.PLANNED:
      return {
        borderColor: palette.routeStates.planned,
        color: isCurrentlySelected
          ? palette.routeStates.planned
          : palette.routeStates.planned,
        backgroundColor: isCurrentlySelected
          ? palette.routeStates.planned
          : "transparent",
      };
    case RouteState.ENDED:
      return {
        borderColor: palette.routeStates.ended,
        color: isCurrentlySelected
          ? palette.routeStates.selectedDarker
          : palette.routeStates.ended,
        backgroundColor: isCurrentlySelected
          ? palette.routeStates.ended
          : "transparent",
      };
    case RouteState.LIVE:
      return {
        borderColor: palette.routeStates.live,
        color: isCurrentlySelected
          ? palette.routeStates.liveDarker
          : palette.routeStates.live,
        backgroundColor: isCurrentlySelected
          ? palette.routeStates.live
          : "transparent",
      };
    default:
      return {
        borderColor: isCurrentlySelected
          ? palette.routeStates.selectedBorder
          : "transparent",
        color: isCurrentlySelected
          ? palette.routeStates.selectedColor
          : "inherit",
        backgroundColor: isCurrentlySelected
          ? palette.routeStates.selectedBorder
          : "transparent",
      };
  }
};

export const isDateSelected = (
  day: any, // Using any since PickersDay uses any type for day
  selectedDay?: DateTime<boolean> | null,
): boolean => {
  if (!selectedDay) return false;

  return (
    day.c.day === selectedDay.day &&
    day.c.month === selectedDay.month &&
    day.c.year === selectedDay.year
  );
};

export const findMatchingDayData = (
  day: any, // Using any since PickersDay uses any type for day
  highlightedDays: AdminGetRouteStatusesPerMonthResDto[],
): AdminGetRouteStatusesPerMonthResDto | undefined => {
  return highlightedDays.find((item) => {
    // Parse the date using Luxon to ensure UTC handling
    // The API returns dates in UTC (e.g., "2026-02-06T00:00:00.000Z")
    // We need to compare the UTC date components, not local time
    const itemDateTime = DateTime.fromISO(item.date, { zone: "utc" });
    const dayDate = day.c.day;
    const dayMonth = day.c.month; // Luxon months are 1-based
    const dayYear = day.c.year;

    return (
      itemDateTime.day === dayDate &&
      itemDateTime.month === dayMonth &&
      itemDateTime.year === dayYear
    );
  });
};

export const getEffectiveState = (
  matchingDay?: AdminGetRouteStatusesPerMonthResDto,
): RouteStateType | undefined => {
  // For selected days, ignore planning state and treat as no state
  return matchingDay?.state === RouteState.PLANNING
    ? undefined
    : matchingDay?.state || undefined;
};

export const hasNonPlanningState = (
  outsideCurrentMonth: boolean,
  matchingDay?: AdminGetRouteStatusesPerMonthResDto,
): boolean => {
  return (
    !outsideCurrentMonth &&
    !!matchingDay &&
    matchingDay.state !== RouteState.PLANNING
  );
};

export const shouldApplyDayStyles = (
  outsideCurrentMonth: boolean,
  hasNonPlanningState: boolean,
  isCurrentlySelected: boolean,
): boolean => {
  return !outsideCurrentMonth && (hasNonPlanningState || isCurrentlySelected);
};

export const getTooltipText = (
  effectiveState: RouteStateType | undefined,
  isCurrentlySelected: boolean,
  hasNonPlanningState: boolean,
): string => {
  // If the current day is also in Planned, Live, or Ended phase → tooltip reflects the phase only
  if (isCurrentlySelected && hasNonPlanningState) {
    if (effectiveState === RouteState.PLANNED) return "Planned";
    if (effectiveState === RouteState.LIVE) return "Live";
    if (effectiveState === RouteState.ENDED) return "Ended";
  }

  // If the date is in Planned phase → tooltip shows "Planned"
  if (effectiveState === RouteState.PLANNED) return "Planned";

  // If the date is in Live phase → tooltip shows "Live"
  if (effectiveState === RouteState.LIVE) return "Live";

  // If the date is in Ended phase → tooltip shows "Ended"
  if (effectiveState === RouteState.ENDED) return "Ended";

  // If the date is the current day and not in Planned, Live, or Ended phase → tooltip shows "Today"
  if (isCurrentlySelected && !hasNonPlanningState) return "Today";

  // No tooltip for other cases
  return "";
};
